import React from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import ResizedImage from "theme/components/atoms/ResizedImage";
import EnhanceProductGallery from "./EnhanceProductGallery";
import ProductGalleryQuery from "./ProductGalleryQuery.gql";
import ErrorBoundary from "theme/components/helpers/ErrorBoundary";
import Swipe from "theme/components/organisms/Swipe";
import { RoundIconButton } from "theme/components/atoms/Button";
import { messages } from "theme/components/organisms/ProductSlider";
import IsDesktop from "theme/utils/isDesktop";
import ImagesGallery from "theme/modules/ProductView/ImagesGallery";
import Stickers from "theme/components/organisms/Stickers/Stickers";
import ImageZoom from "theme/components/atoms/Image/ImageZoom";

const NB_IMAGE_SLIDER = 6;

const DesktopGallery = ({
  mainImage,
  images,
  name,
  previewMainImageUrl,
  isNew,
  stickers,
}) => {
  const [index, setIndex] = React.useState(0);
  const [startIndex, setStartIndex] = React.useState(0);
  const sliderImages = images.slice(startIndex, startIndex + NB_IMAGE_SLIDER);

  const current = images[startIndex + index];
  const hasPrevious = startIndex > 0;
  const hasNext = images.length - startIndex > NB_IMAGE_SLIDER;

  const haveSlider = images.length > NB_IMAGE_SLIDER;

  if (!current) {
    if (!mainImage) {
      return (
        <div className="product__gallery">
          <div className="product__gallery__slider">
            <div className="product__gallery__slider__image"></div>
          </div>
          <div className="image">
            <Stickers isNew={isNew} stickers={stickers} />
            <ResizedImage
              key="main"
              className="product__gallery__image"
              itemProp="image"
              src={mainImage && mainImage.file}
              alt={(mainImage && mainImage.alt) || name}
              format="medium"
            />
          </div>
        </div>
      );
    }
    return (
      <div className="product__gallery">
        <div className="product__gallery__slider">
          <div className="product__gallery__slider__image"></div>
        </div>
        <div className="image">
          <Stickers isNew={isNew} stickers={stickers} />
          <ImageZoom
            alt={(mainImage && mainImage.alt) || name}
            smallImageFile={mainImage && mainImage.file}
            largeImageFile={mainImage && mainImage.file}
          />
        </div>
        <ResizedImage
          key="main"
          className="product__gallery__image"
          itemProp="image"
          src={mainImage && mainImage.file}
          alt={(mainImage && mainImage.alt) || name}
          format="medium"
        />
      </div>
    );
  }
  return (
    <div className="product__gallery">
      {images && images.length > 0 && (
        <ImagesGallery
          haveSlider={haveSlider}
          name={name}
          images={sliderImages}
          onImageClick={setIndex}
          hasPrev={hasPrevious}
          hasNext={hasNext}
          currentIndex={index}
          startIndex={startIndex}
          setStartIndex={setStartIndex}
        />
      )}
      <div className="image">
        <Stickers isNew={isNew} stickers={stickers} />
        <ImageZoom
          alt={(current && current.alt) || name}
          smallImageFile={
            previewMainImageUrl ? previewMainImageUrl : current && current.file
          }
          largeImageFile={
            previewMainImageUrl ? previewMainImageUrl : current && current.file
          }
        />
      </div>
    </div>
  );
};

const MobileGallery = ({ mainImage, images, name, intl, isNew, stickers }) => {
  const [index, setIndex] = React.useState(0);
  const previous = images[index - 1];
  const current = images[index];
  const next = images[index + 1];
  const progressGap = 100 / images.length;

  if (!current && mainImage) {
    return (
      <div className="product__gallery">
        <Stickers isNew={isNew} stickers={stickers} />
        <div className="product__gallery__image" itemProp="image">
          <ImageZoom
            alt={(mainImage && mainImage.alt) || name}
            smallImageFile={mainImage && mainImage.file}
            largeImageFile={mainImage && mainImage.file}
          />
        </div>
      </div>
    );
  } else if (!current) {
    return "";
  }

  return (
    <div className="product__gallery">
      <Stickers isNew={isNew} stickers={stickers} />
      <div className="product__gallery__slider">
        <div className="product__gallery__previous">
          <RoundIconButton
            state={index <= 0 ? "disabled" : null}
            onDisableClick={() => {}}
            onClick={() => setIndex(index - 1)}
            icon="chevron"
            iconSize="tiny"
            title={intl.formatMessage(messages.previous)}
          />
        </div>
        <Swipe
          current={
            <ImageZoom
              alt={(current && current.alt) || name}
              smallImageFile={current && current.file}
              largeImageFile={current && current.file}
            />
          }
          previous={
            previous && (
              <ResizedImage
                key={previous.id}
                className="product__gallery__image"
                itemProp="image"
                src={previous && previous.file}
                alt={(previous && previous.alt) || name}
                format="medium"
              />
            )
          }
          next={
            next && (
              <ResizedImage
                key={next.id}
                className="product__gallery__image"
                itemProp="image"
                src={next && next.file}
                alt={(next && next.alt) || name}
                format="medium"
              />
            )
          }
          onNext={next ? () => setIndex(index + 1) : null}
          onPrevious={previous ? () => setIndex(index - 1) : null}
        />
        <div className="product__gallery__position-wrapper">
          <div className="product__gallery__position">
            <div
              className="product__gallery__position__current"
              style={{
                width: `${progressGap}%`,
                left: `${progressGap * index}%`,
              }}
            />
          </div>
        </div>
        <div className="product__gallery__next">
          <RoundIconButton
            state={index >= images.length - 1 ? "disabled" : null}
            onDisableClick={() => {}}
            onClick={() => setIndex(index + 1)}
            icon="chevron"
            iconSize="tiny"
            title={intl.formatMessage(messages.next)}
          />
        </div>
      </div>
    </div>
  );
};

const ProductGallery = ({
  mainImage,
  previewMainImageUrl,
  images,
  name,
  intl,
  isNew,
  stickers,
  sku,
}) => {
  return (
    <ErrorBoundary>
      {({ hasError }) =>
        // Fake the main image with a placeholder and hide the gallery
        hasError ? (
          <div className="product__gallery" />
        ) : (
          <IsDesktop>
            {(isDesktop) =>
              isDesktop ? (
                <DesktopGallery
                  key={`desktop-gallery-${sku}`}
                  mainImage={mainImage}
                  previewMainImageUrl={previewMainImageUrl}
                  images={images}
                  intl={intl}
                  name={name}
                  isNew={isNew}
                  stickers={stickers}
                />
              ) : (
                <MobileGallery
                  key={`mobile-gallery-${sku}`}
                  mainImage={mainImage}
                  images={images}
                  intl={intl}
                  name={name}
                  isNew={isNew}
                  stickers={stickers}
                />
              )
            }
          </IsDesktop>
        )
      }
    </ErrorBoundary>
  );
};

ProductGallery.propTypes = {
  mainImage: PropTypes.object,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any.isRequired,
      file: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    })
  ),
  name: PropTypes.string,
};

const SmartGallery = EnhanceProductGallery({ ProductGalleryQuery })(
  ProductGallery
);

export default injectIntl(SmartGallery);
